import { useCallback, useEffect, useState } from 'react';
import { Save, RotateCcw } from 'lucide-react';
import type { Language } from '../i18n';
import { ui } from '../lib/localization';
import { localeFor } from '../lib/languages';
import { MAX_SITE_PROMPT_CHARACTERS, type SitePromptSnapshot } from '../lib/site-prompt';

export function AdminSystemPrompt({ language }: { language: Language }) {
  const [snapshot, setSnapshot] = useState<SitePromptSnapshot | null>(null);
  const [draft, setDraft] = useState('');
  const [reset, setReset] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'load' | 'save' | 'conflict' | 'auth' | null>(null);
  const [saved, setSaved] = useState(false);
  const accept = (value: SitePromptSnapshot) => {
    setSnapshot(value); setDraft(value.prompt); setReset(false);
  };
  const load = useCallback(async () => {
    setBusy(true); setError(null); setSaved(false);
    try {
      const response = await fetch('/api/admin/system-prompt', { cache: 'no-store' });
      if (!response.ok) { setError(response.status === 401 || response.status === 403 ? 'auth' : 'load'); return; }
      accept(await response.json() as SitePromptSnapshot);
    } catch { setError('load'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const dirty = !!snapshot && (draft !== snapshot.prompt || reset && !snapshot.isDefault);
  const save = async () => {
    if (!snapshot || !dirty || busy || !draft.trim()) return;
    setBusy(true); setError(null); setSaved(false);
    try {
      const response = await fetch('/api/admin/system-prompt', {
        method: 'PUT', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ prompt: reset ? null : draft, revision: snapshot.revision }),
      });
      if (!response.ok) { setError(response.status === 409 ? 'conflict' : response.status === 401 || response.status === 403 ? 'auth' : 'save'); return; }
      accept(await response.json() as SitePromptSnapshot); setSaved(true);
    } catch { setError('save'); }
    finally { setBusy(false); }
  };
  return <section className="admin-panel admin-prompt" aria-labelledby="admin-prompt-title" aria-busy={busy}>
    <div className="admin-panel-heading"><div>
      <small>{ui(language, 'Site admin')}</small>
      <h2 id="admin-prompt-title">{ui(language, 'Site system prompt')}</h2>
    </div></div>
    <div className="admin-prompt-body">
      <p id="admin-prompt-help">{ui(language, 'Controls the main instructions for chats and scheduled tasks. Saved changes apply to the next request, including existing conversations. Conversation-specific instructions are kept.')}</p>
      {snapshot ? <>
        <textarea id="admin-prompt-input" aria-labelledby="admin-prompt-title" aria-describedby="admin-prompt-help" value={draft} maxLength={MAX_SITE_PROMPT_CHARACTERS} disabled={busy} spellCheck={false} rows={12}
          onChange={event => { setDraft(event.target.value); setReset(false); setSaved(false); }} />
        <div className="admin-prompt-meta"><span>{ui(language, '{0} / {1} characters', draft.length.toLocaleString(localeFor(language)), MAX_SITE_PROMPT_CHARACTERS.toLocaleString(localeFor(language)))}</span>
          <span>{snapshot.isDefault ? ui(language, 'Using built-in instructions') : ui(language, 'Using saved instructions')}{snapshot.updatedAt ? ` · ${ui(language, 'Updated {0}', new Date(snapshot.updatedAt).toLocaleString(localeFor(language)))}` : ''}</span></div>
        <div className="admin-prompt-actions">
          <button type="button" className="admin-prompt-save" disabled={busy || !dirty || !draft.trim()} onClick={() => void save()}><Save size={16} />{busy ? ui(language, 'Saving…') : ui(language, 'Save system prompt')}</button>
          <button type="button" disabled={busy} onClick={() => { setDraft(snapshot.defaultPrompt); setReset(true); setSaved(false); }}>{<RotateCcw size={16} />}{ui(language, 'Restore default draft')}</button>
          {dirty && <span>{ui(language, 'Unsaved changes')}</span>}
        </div>
      </> : busy ? <p role="status">{ui(language, 'Loading system prompt…')}</p> : null}
      {error && <div className="admin-prompt-error" role="alert">
        <p>{error === 'conflict' ? ui(language, 'The system prompt changed in another session. Your draft is kept. Reload the saved version before editing again.')
          : error === 'auth' ? ui(language, 'Administrator Cloudflare account required')
          : error === 'load' ? ui(language, 'Could not load the system prompt. Your draft has not changed.')
          : ui(language, 'Could not save the system prompt. Your draft is kept; please retry.')}</p>
        {error === 'load' || error === 'conflict' ? <button type="button" disabled={busy} onClick={() => void load()}>{error === 'conflict' ? ui(language, 'Discard draft and reload') : ui(language, 'Try again')}</button> : error === 'auth' ? <a href="/api/auth/cloudflare/start?returnTo=/admin">{ui(language, 'Connect Cloudflare')}</a> : null}
      </div>}
      {saved && <p role="status" className="admin-prompt-success">{ui(language, 'System prompt saved. It will apply to the next request.')}</p>}
    </div>
  </section>;
}
